import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { MultiImageUploader } from '../../components/ImageUploader';
import { resolveImageUrl } from '../../lib/imageUrl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProducts() {
  const { t, getLocalizedContent } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: { fr: '', en: '' },
    description: { fr: '', en: '' },
    category: '',
    price: 0,
    images: [],
    in_stock: true,
    is_featured: false,
    is_active: true
  });
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/products?active_only=false`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/products/${editingItem.id}`, formData);
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API}/admin/products`, formData);
        toast.success('Produit créé');
      }
      setShowModal(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`);
      toast.success('Produit supprimé');
      fetchItems();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || { fr: '', en: '' },
      description: item.description || { fr: '', en: '' },
      category: item.category || '',
      price: item.price || 0,
      images: item.images || [],
      in_stock: item.in_stock !== false,
      is_featured: item.is_featured || false,
      is_active: item.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: { fr: '', en: '' },
      description: { fr: '', en: '' },
      category: '',
      price: 0,
      images: [],
      in_stock: true,
      is_featured: false,
      is_active: true
    });
    setImageUrl('');
  };

  const addImage = () => {
    if (imageUrl) {
      setFormData({ ...formData, images: [...formData.images, imageUrl] });
      setImageUrl('');
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">
          {t('admin_products')}
        </h1>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('admin_new')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((item, index) => (
            <Card key={item.id} className="border-0 shadow-sm overflow-hidden">
              <div className="relative h-40">
                <img
                  src={resolveImageUrl(item.images?.[0]) || 'https://via.placeholder.com/300x200'}
                  alt={getLocalizedContent(item.name)}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2 flex gap-1">
                  {!item.in_stock && <Badge variant="destructive">Rupture</Badge>}
                  {item.is_featured && <Badge className="bg-[#E50E24]">Vedette</Badge>}
                </div>
              </div>
              <CardContent className="p-4">
                <Badge variant="outline" className="mb-2 text-xs">{item.category}</Badge>
                <h3 className="font-semibold text-[#0F172A] mb-1 line-clamp-1">{getLocalizedContent(item.name)}</h3>
                <p className="text-lg font-bold text-[#0B6AB3]">{item.price?.toLocaleString()} FCFA</p>
                <div className="flex items-center justify-between mt-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {item.is_active ? 'Actif' : 'Inactif'}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifier le produit' : 'Nouveau produit'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nom (FR) *</Label>
                <Input
                  value={formData.name.fr}
                  onChange={(e) => setFormData({ ...formData, name: { ...formData.name, fr: e.target.value } })}
                  required
                />
              </div>
              <div>
                <Label>Nom (EN)</Label>
                <Input
                  value={formData.name.en}
                  onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Description (FR) *</Label>
                <Textarea
                  value={formData.description.fr}
                  onChange={(e) => setFormData({ ...formData, description: { ...formData.description, fr: e.target.value } })}
                  rows={3}
                  required
                />
              </div>
              <div>
                <Label>Description (EN)</Label>
                <Textarea
                  value={formData.description.en}
                  onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                  rows={3}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Catégorie *</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="informatique, equipement..."
                  required
                />
              </div>
              <div>
                <Label>Prix (FCFA) *</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
            </div>
            <div>
              <MultiImageUploader
                label="Images du produit"
                values={formData.images}
                onChange={(imgs) => setFormData({ ...formData, images: imgs })}
                testIdPrefix="product-images"
              />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.in_stock}
                  onCheckedChange={(v) => setFormData({ ...formData, in_stock: v })}
                />
                <Label>En stock</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_featured}
                  onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })}
                />
                <Label>Produit vedette</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Actif</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                {t('common_cancel')}
              </Button>
              <Button type="submit" className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">
                {t('common_save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
